import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import {
  ProtocolCardChain,
  ProtocolCardIdentity,
} from '@/components/admin/protocoles/protocol-card';
import { filterControlClass } from '@/components/admin/versioned/filter-field';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import { Button } from '@/components/ui/button';
import type { ProtocolCard } from '@matter_extractor/shared';
import { useId, useState } from 'react';

/**
 * Le sélecteur de protocole de la page protocole (refonte modulaire §9.5 ;
 * maquette `Timeline.dc.html` ; issue #57).
 *
 * §9.5: « le sélecteur lit la liste réelle des protocoles et dessine la carte de
 * §7.1 depuis ce que la carte du protocole porte déjà (sa description, ses
 * étapes dans l'ordre, #48) : rien n'est redessiné de ce côté ». So the two card
 * blocks are `/admin/protocoles`' own, imported: a protocol has to look the same
 * wherever it is read, and a second drawing here would be a second protocol.
 *
 * It is the twin of `experience-persona-picker.tsx` and the resemblance is the
 * point - one gesture, « choisir un objet d'une autre bibliothèque », drawn
 * once. What is NOT reused from either side is `VersionedCardTile`: a card of a
 * selector is a CHOICE and not a link, so it carries a button where the
 * library's carries a chevron.
 *
 * ## A new choice is `latest`, and the version is chosen AFTER (vitrine.md V16)
 *
 * The selector used to carry a « Tag joué » select, the default of the choice to
 * come. A reference now starts at `latest` whatever is chosen - épingler est
 * l'exception - and the pill-menu of the chosen protocol (`ReferenceControl`, on
 * the card of the page and in the scene's vignette) is where a precise version
 * is picked, so the selector asks nothing of the sort.
 *
 * ## « Sans protocole pour l'instant » is a CHOICE, not an absence
 *
 * §9.1 permits an empty protocol page and says what it costs: « la page
 * protocole vide reste permise, et se saute en session ... "Passer en
 * production" ajoute deux vérifications ». So clearing is a button of its own
 * beside the cards, and not something an operator reaches by deleting a row -
 * there is no row.
 *
 * ## The retired ones are absent, and the search is local
 *
 * §4 takes a retired protocol out of every selector. The search filters what is
 * already in memory, like every filter of a library: the whole list arrives in
 * one read, so a keystroke costs a `filter()`.
 */

export interface ExperienceProtocolPickerProps {
  /** The library, retired ones included - this component drops them (§4). */
  readonly protocols: readonly ProtocolCard[];
  /** Whether the read has landed: an empty list means « rien » only once it has. */
  readonly loaded: boolean;
  /** The reference in force, so the card that is already chosen says so. */
  readonly chosenId: string | null;
  readonly onChoose: (protocolId: string) => void;
  /** « Sans protocole pour l'instant » - the page stays, empty (§9.1). */
  readonly onClear: () => void;
  readonly onClose: () => void;
}

export function ExperienceProtocolPicker({
  protocols,
  loaded,
  chosenId,
  onChoose,
  onClear,
  onClose,
}: ExperienceProtocolPickerProps) {
  const searchId = useId();
  const [search, setSearch] = useState('');

  const needle = search.trim().toLowerCase();
  const shown = protocols.filter(
    (card) => card.retiredAt === null && card.name.toLowerCase().includes(needle),
  );

  return (
    <div
      data-testid={EXPERIENCE_IDS.protocolPicker}
      className="space-y-4 rounded-md border border-gray-100 bg-gray-50 p-4"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex max-w-full flex-col gap-1">
          <label
            htmlFor={searchId}
            className="font-semibold text-gray-600 text-xs uppercase tracking-wide"
          >
            Chercher un protocole
          </label>
          <input
            id={searchId}
            data-testid={EXPERIENCE_IDS.protocolSearch}
            type="search"
            value={search}
            placeholder="Rechercher"
            onChange={(event) => setSearch(event.currentTarget.value)}
            className={filterControlClass(needle !== '', 'w-64 max-w-full')}
          />
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-gray-700 text-sm">
          {loaded
            ? 'Aucun protocole ne répond à cette recherche. La bibliothèque se remplit dans Protocoles.'
            : 'Lecture de la bibliothèque de protocoles…'}
        </p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((card) => (
            <li
              key={card.id}
              data-testid={EXPERIENCE_IDS.protocolOption}
              data-chosen={card.id === chosenId ? 'true' : 'false'}
              className={`flex flex-col gap-3 rounded-md border-2 bg-white p-4 ${
                card.id === chosenId ? 'border-bleu-nuit' : 'border-gray-200'
              }`}
            >
              <ProtocolCardIdentity card={card} />
              <ProtocolCardChain card={card} />
              <div className="flex flex-wrap gap-2">
                {/* ABSENTE, jamais un « v- » : rien n'a été passé en production (§4). */}
                {card.production === null ? null : (
                  <VersionPill
                    entity="protocole"
                    tag="production"
                    version={card.production.number}
                  />
                )}
                <VersionPill
                  entity="protocole"
                  tag="development"
                  version={card.development.number}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-auto self-start"
                onClick={() => onChoose(card.id)}
              >
                Choisir
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={EXPERIENCE_IDS.protocolClear}
          onClick={onClear}
        >
          Sans protocole pour l’instant
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Fermer le sélecteur
        </Button>
      </div>
    </div>
  );
}
